import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AdvancedEditor } from "./AdvancedEditor";
import EmailSendLogSection from "./EmailSendLogSection";
import { Mail, Plus, Pencil, Trash2, Send, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  template_id: null, name: "", event: "", subject: "", body_html: "",
  from_email: "", from_name: "", is_active: true,
};

export default function EmailTemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testingId, setTestingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, eRes] = await Promise.all([
        axios.get(`${API}/api/admin/email-templates`, { withCredentials: true }),
        axios.get(`${API}/api/admin/email-templates/events`, { withCredentials: true }),
      ]);
      setTemplates(tRes.data.templates || []);
      setEvents(eRes.data.events || []);
    } catch (e) {
      toast.error("Failed to load email templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const eventInfo = (key) => events.find((e) => e.key === key);

  const openCreate = () => {
    setForm({ ...emptyForm, event: events[0]?.key || "" });
    setDialogOpen(true);
  };

  const openEdit = (tpl) => {
    setForm({
      template_id: tpl.template_id, name: tpl.name, event: tpl.event, subject: tpl.subject,
      body_html: tpl.body_html, from_email: tpl.from_email || "", from_name: tpl.from_name || "",
      is_active: tpl.is_active,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.event || !form.subject.trim()) {
      toast.error("Name, event and subject are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name, event: form.event, subject: form.subject, body_html: form.body_html,
        from_email: form.from_email, from_name: form.from_name, is_active: form.is_active,
      };
      if (form.template_id) {
        await axios.put(`${API}/api/admin/email-templates/${form.template_id}`, payload, { withCredentials: true });
        toast.success("Email template updated");
      } else {
        await axios.post(`${API}/api/admin/email-templates`, payload, { withCredentials: true });
        toast.success("New automatic email added");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (tpl, checked) => {
    try {
      await axios.put(`${API}/api/admin/email-templates/${tpl.template_id}`, { is_active: checked }, { withCredentials: true });
      setTemplates((prev) => prev.map((t) => (t.template_id === tpl.template_id ? { ...t, is_active: checked } : t)));
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (tpl) => {
    try {
      await axios.delete(`${API}/api/admin/email-templates/${tpl.template_id}`, { withCredentials: true });
      toast.success("Email deleted");
      setTemplates((prev) => prev.filter((t) => t.template_id !== tpl.template_id));
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handleSendTest = async (tpl) => {
    if (!testTo.trim()) {
      toast.error("Enter a test recipient email first");
      return;
    }
    setTestingId(tpl.template_id);
    try {
      await axios.post(`${API}/api/admin/email-templates/${tpl.template_id}/test`, { to: testTo }, { withCredentials: true });
      toast.success(`Test email sent to ${testTo}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    } finally {
      setTestingId(null);
    }
  };

  const selectedEventVars = eventInfo(form.event)?.variables || [];

  return (
    <div className="space-y-6" data-testid="email-templates-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-xl font-semibold flex items-center gap-2"><Mail size={20} /> Emails</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Every automated system email — edit subject, body, sender and status, or add an extra email for an existing event.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="test-send@example.com"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            className="w-56 h-9 text-sm"
            data-testid="email-test-recipient-input"
          />
          <Button onClick={openCreate} className="rounded-none" data-testid="add-email-template-btn">
            <Plus size={16} className="mr-1" /> Add Email
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin" /></div>
      ) : (
        <div className="border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left">
                <th className="p-3 font-medium">Name</th>
                <th className="p-3 font-medium">Event</th>
                <th className="p-3 font-medium">Subject</th>
                <th className="p-3 font-medium">From</th>
                <th className="p-3 font-medium">Active</th>
                <th className="p-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((tpl) => (
                <tr key={tpl.template_id} className="border-t border-border" data-testid={`email-template-row-${tpl.template_id}`}>
                  <td className="p-3 font-medium">{tpl.name}</td>
                  <td className="p-3 text-muted-foreground">{tpl.event_label}</td>
                  <td className="p-3 text-muted-foreground truncate max-w-[260px]">{tpl.subject}</td>
                  <td className="p-3 text-muted-foreground">{tpl.from_email || "Default"}</td>
                  <td className="p-3">
                    <Switch
                      checked={tpl.is_active}
                      onCheckedChange={(checked) => handleToggleActive(tpl, checked)}
                      data-testid={`email-template-active-toggle-${tpl.template_id}`}
                    />
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        onClick={() => handleSendTest(tpl)}
                        disabled={testingId === tpl.template_id}
                        title="Send test"
                        data-testid={`email-template-test-btn-${tpl.template_id}`}
                      >
                        {testingId === tpl.template_id ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        onClick={() => openEdit(tpl)}
                        title="Edit"
                        data-testid={`email-template-edit-btn-${tpl.template_id}`}
                      >
                        <Pencil size={15} />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Delete"
                            data-testid={`email-template-delete-btn-${tpl.template_id}`}>
                            <Trash2 size={15} />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete "{tpl.name}"?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This email will no longer be sent for the "{tpl.event_label}" event.
                              {templates.filter((t) => t.event === tpl.event).length === 1 &&
                                " This is the only email for this event — deleting it stops that notification entirely."}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              onClick={() => handleDelete(tpl)}
                              data-testid={`email-template-delete-confirm-${tpl.template_id}`}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="border-t border-border pt-6">
        <EmailSendLogSection />
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="email-template-dialog">
          <DialogHeader>
            <DialogTitle>{form.template_id ? "Edit Email" : "Add Automatic Email"}</DialogTitle>
            <DialogDescription>
              {form.template_id
                ? "Changes take effect immediately for the next time this event fires."
                : "This adds an extra email that fires alongside any existing ones for the event you pick."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs mb-1 block">Name</Label>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="email-template-name-input" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Event (when it sends)</Label>
                <Select value={form.event} onValueChange={(v) => setForm((f) => ({ ...f, event: v }))}
                  disabled={!!form.template_id}>
                  <SelectTrigger data-testid="email-template-event-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {events.map((ev) => (
                      <SelectItem key={ev.key} value={ev.key}>{ev.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {eventInfo(form.event)?.description && (
              <p className="text-xs text-muted-foreground">{eventInfo(form.event).description}</p>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs mb-1 block">From email (optional override)</Label>
                <Input placeholder="Uses global sender if blank" value={form.from_email}
                  onChange={(e) => setForm((f) => ({ ...f, from_email: e.target.value }))}
                  data-testid="email-template-from-email-input" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">From name (optional)</Label>
                <Input value={form.from_name} onChange={(e) => setForm((f) => ({ ...f, from_name: e.target.value }))}
                  data-testid="email-template-from-name-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Subject</Label>
              <Input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                data-testid="email-template-subject-input" />
            </div>
            {selectedEventVars.length > 0 && (
              <div className="bg-muted/50 rounded-sm p-3">
                <p className="text-xs font-medium mb-1">Available variables — use like {"{{ variable }}"}</p>
                <div className="flex flex-wrap gap-1.5">
                  {selectedEventVars.map((v) => (
                    <code key={v} className="text-xs bg-background border border-border px-1.5 py-0.5 rounded">{`{{ ${v} }}`}</code>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs mb-1 block">Body</Label>
              <AdvancedEditor value={form.body_html} onChange={(v) => setForm((f) => ({ ...f, body_html: v }))} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
                data-testid="email-template-active-switch" />
              <Label className="text-sm">Active (send this email when the event fires)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="email-template-cancel-btn">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="email-template-save-btn">
              {saving ? <Loader2 size={16} className="animate-spin mr-1" /> : null}
              {form.template_id ? "Save Changes" : "Add Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
